<script lang="ts" setup>
    import '../../../css/choice.css';
    import {onMounted,ref,reactive} from 'vue';  
    import {popAlert} from '../../lib/Notification'; 
    import {ILockboxInputs,GetPatientReport,GetInsuranceReport,
            IInsuranceLockboxReport,IPatientLockboxReport,LockboxReportType,LockboxDateType,
            LockboxStatusType,LockboxErrorMessage} from '../../lib/Models/LockboxReportModel';
    import {ButtonDisplaySize,DateDisplayFormat,AlignmentType} from "../../Enums/UiEnums";
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import {ICol, RenderModeType} from "../../lib/ChoiceTableHelpers" ;
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import {ActionButtonStyle} from '../../lib/Types/Types';
    import {NoTzDate,toDisplayDateString  } from '../../lib/DateUtils';
    import {NumberToLocaleString} from '../../lib/Formatting/Number' 
 
    const reportTable = ref<InstanceType<typeof ChoiceTable>>(); 
    const reportColumns = ref<ICol[]>([]); 

    const patientColumns = ref<ICol[]>([
        { Name: 'Vendor Name', DataField: 'vendorName'},
        { Name: 'File Name', DataField: 'fileName'},
        { Name: 'Lockbox Date (EDT)', DataField: 'lockBoxDateTimeEDT', Alignment: AlignmentType.Center},
        { Name: 'Chart Number', DataField: 'chart'},
        { Name: 'Patient Name', DataField: 'patientName'},
        { Name: 'Statement ID 1', DataField: 'statementID1'},
        { Name: 'Statement ID 2', DataField: 'statementID2'},
        { Name: 'Deposit Date', DataField: 'depositDate', Alignment: AlignmentType.Center},
        { Name: 'Deposit Check', DataField: 'depositCheck'},
        { Name: 'Deposit Amount', DataField: 'depositAmount', Alignment: AlignmentType.Right, RenderMode: RenderModeType.AccountingMoney},
        { Name: 'Payment Type ID', DataField: 'paymentType'},
        { Name: 'Payment Type', DataField: 'paymentTypeDescription'},
        { Name: 'Payment Method ID', DataField: 'paymentMethod'},
        { Name: 'Payment Method', DataField: 'paymentMethodDescription'},
        { Name: 'Status Message', DataField: 'fileStatus', RenderMode: RenderModeType.HTML}
    ]);
    const insuranceColumns = ref<ICol[]>([
        { Name: 'Vendor Name', DataField: 'vendorName'},
        { Name: 'File Name', DataField: 'fileName'},
        { Name: 'Lockbox Date', DataField: 'lockBoxDateTimeEDT', Alignment: AlignmentType.Center},
        { Name: 'Status Message', DataField: 'fileStatus', RenderMode: RenderModeType.HTML}
    ]);
    const formData = reactive <
    {    
        setup: boolean,
        maxRecords: number,
        reportName: string,
        reportData? : IInsuranceLockboxReport[] | IPatientLockboxReport[],
        footerDisplay: string,
        searchFields: {StartDate: NoTzDate, EndDate: NoTzDate, DateType: LockboxDateType, StatusType: LockboxStatusType},
        window: {headerHeight: number,   footerHeight: number, windowPadding: number, bodyHeight: number},
        buttons: {ExportDisabled: boolean}
    }>({
        setup: true,
        maxRecords: 2500,
        reportName: "",
        footerDisplay: "",
        searchFields: {StartDate: "", EndDate: "", DateType: LockboxDateType.Lockbox, StatusType: LockboxStatusType.All},
        window: {headerHeight: 6.5, footerHeight: 3, windowPadding: 1.5,  bodyHeight: 10},
        buttons: {ExportDisabled: true}
    }); 
    const props = withDefaults(defineProps<{ 
        reportType: LockboxReportType,
        dateType: LockboxDateType,
        statusType: LockboxStatusType,
        startDate?: NoTzDate,
        endDate?: NoTzDate
        lockBoxReport:  IInsuranceLockboxReport[] | IPatientLockboxReport[] 
    }>(),{});
    onMounted(async () => {   
        formData.searchFields.DateType = props.dateType;
        formData.searchFields.StatusType = props.statusType;
        formData.reportName = (props.reportType === LockboxReportType.Insurance ? "Insurance" : "Patient");
        if (props.startDate) {
            formData.searchFields.StartDate = (toDisplayDateString(props.startDate,DateDisplayFormat.NumericDashDate) ?? "");
        }
        if (props.endDate) {
            formData.searchFields.EndDate = (toDisplayDateString(props.endDate,DateDisplayFormat.NumericDashDate) ?? "");
        } 
        if (props.lockBoxReport) {
            if (props.reportType === LockboxReportType.Insurance) {
                formData.reportData = props.lockBoxReport; 
                reportColumns.value = insuranceColumns.value;
            } 
            else {
                formData.reportData = props.lockBoxReport; 
                reportColumns.value = patientColumns.value;
            } 
        }
        enableButtons();
        resetHeights();
        window.addEventListener('resize', () => {resetHeights();}); 
        formData.setup = false; 
        checkRecordCount();
        document.title = `${formData.reportName} ePayments`;
    });  
    function enableButtons() : void { 
        formData.buttons.ExportDisabled = (!formData.reportData?.length);
    } 
    function resetHeights() : void {
        const window_height = window.innerHeight / 16;
        formData.window.bodyHeight = window_height - formData.window.headerHeight - formData.window.footerHeight - formData.window.windowPadding; 
    }
    async function cancelProcess() : Promise<void>  {
        window.close();
    } 
    async function getNewSearchResults() : Promise<void> {  
        if (formData.setup) {
            return;
        }
        const searchFields : ILockboxInputs = {ReportType : props.reportType, DateType: formData.searchFields.DateType, StatusType: formData.searchFields.StatusType, StartDate: formData.searchFields.StartDate ?? "", EndDate: formData.searchFields.EndDate ?? ""};
        if (props.reportType === LockboxReportType.Insurance) {
            const respGet = await GetInsuranceReport(searchFields);  
            if (!respGet.success) {  
                popAlert(respGet.error?.error ?? LockboxErrorMessage.Insurance,"ePayments Results");
                return;
            } 
            formData.reportData = respGet.success; 
        }   
        else {
            const respGet = await GetPatientReport(searchFields);  
            if (!respGet.success) {  
                popAlert(respGet.error?.error ?? LockboxErrorMessage.Patient,"ePayments Results");
                return;
            } 
            formData.reportData= respGet.success; 
        }
        checkRecordCount();
        enableButtons(); 
    }     
    async function exportReport() : Promise<void>  {
        const report_start_date = (formData.searchFields.StartDate ?? "");
        const report_end_date = (formData.searchFields.EndDate ?? ""); 
        const sheet_name = `${formData.reportName} ePayments`;
        const file_name = `${formData.reportName}_ePayments_${report_start_date}_${report_end_date}.xls`;
        reportTable.value?.exportToExcel(file_name, sheet_name);
    } 
    function checkRecordCount() {
        const total_records = (formData.reportData?.length ?? 0);
        if (total_records === 0) {
            formData.footerDisplay = "No data was found matching your search criteria."
        }
        else {
            formData.footerDisplay = `${NumberToLocaleString(total_records)} record${(total_records === 1 ? "" : "s")} displayed`;
        }
        if (total_records >= formData.maxRecords) {
            popAlert(`You have hit the max limit of ${NumberToLocaleString(formData.maxRecords)} records, this may mean there are more records but you will need to narrow your search criteria.`,`${formData.reportName} ePayments Results`); 
        }
    }
</script>
<template>
    <div>
        <div class="row">
            <div class="col" style="font-size: .75rem;"> 
                <span class="fs-6 fw-bold text-isalus-primary pe-3">Search Criteria</span>
            </div>
        </div>
        <div class="row"> 
            <div class="col" style="font-size: .75rem;">  
                <table>
                    <tr height ="45px">
                        <td class="label-isalus-small text-isalus-primary px-sm-2">Start Date:</td>
                        <td class="date-isalus-small">
                            <DatePickerNoTZ
                                v-model:value="formData.searchFields.StartDate" 
                                :clearable="true" 
                                :required=false
                                :errorDisplay=false  
                                :placeholder="'Start Date'"
                                :cssFramework="'vanilla'" 
                                @update:value="()=>getNewSearchResults()"
                                />  
                        </td>
                        <td class="label-isalus-small text-isalus-primary px-sm-2">End Date:</td>
                        <td class="date-isalus-small">
                            <span class="date-isalus-small">
                            <DatePickerNoTZ
                                v-model:value="formData.searchFields.EndDate" 
                                :clearable="true" 
                                :required=false
                                :errorDisplay=false  
                                :placeholder="'End Date'"
                                :cssFramework="'vanilla'"
                                @update:value="()=>getNewSearchResults()"
                                /> </span>
                        </td>
                        <td class="label-isalus-small text-isalus-primary px-sm-2">Using:</td>
                        <td>
                            <input v-model="formData.searchFields.DateType" :disabled="props.reportType === LockboxReportType.Insurance" type="radio" name="searchDateType" id="depositDate" :value="LockboxDateType.Deposit" @change="getNewSearchResults">
                            <label class="fw-bold label-isalus-small px-2" for="depositDate">Deposit Dates</label>

                            <input v-model="formData.searchFields.DateType" :disabled="props.reportType === LockboxReportType.Insurance"  type="radio" name="searchDateType" id="lockboxDate" :value="LockboxDateType.Lockbox" @change="getNewSearchResults">
                            <label class="fw-bold label-isalus-small px-2" for="lockboxDate">Lockbox Dates</label>
                        </td>
                    </tr>
                    <tr>
                        <td class="label-isalus-small text-isalus-primary px-sm-2">Status Filter:</td>
                        <td colspan="5">
                            <input v-model="formData.searchFields.StatusType" type="radio" name="searchStatus" id="statusAll" :value="LockboxStatusType.All" @change="getNewSearchResults">
                            <label class="fw-bold label-isalus-small px-2" for="statusAll">All Statuses</label>

                            <input v-model="formData.searchFields.StatusType" type="radio" name="searchStatus" id="statusError" :value="LockboxStatusType.Error" @change="getNewSearchResults">
                            <label class="fw-bold label-isalus-small px-2" for="statusError">Error Statuses</label>

                            <input v-model="formData.searchFields.StatusType" type="radio" name="searchStatus" id="statusSuccess" :value="LockboxStatusType.Success"  @change="getNewSearchResults">
                            <label class="fw-bold label-isalus-small px-2" for="statusSuccess">Success Statuses</label> 

                            <input v-model="formData.searchFields.StatusType" type="radio" name="searchStatus" id="statusUnprocessed" :value="LockboxStatusType.Unprocessed"  @change="getNewSearchResults">
                            <label class="fw-bold label-isalus-small px-2" for="statusUnprocessed">Unprocessed</label> 
                        </td>
                    </tr>
                </table>
            </div> 
        </div>
    </div>
    <div class="row pt-1 border-top"> 
        <div class="col label-isalus-small text-isalus-primary skinnyscroll pt-1" :style="{'overflow-y': 'scroll', 'overflow-x': 'scroll', 'height': (formData.window.bodyHeight) + 'rem'}">
            <ChoiceTable 
                ref="reportTable"
                :columns="reportColumns" 
                :data="formData.reportData" 
                :show-filters="false" 
                :allowSorting="false"
                key-value-data-member="ID"
                >
            </ChoiceTable>  
        </div> 
    </div>  
    <div class="row border-top pt-2" :style="{'overflow': 'hidden', 'height': (formData.window.footerHeight) + 'rem'}"> 
        <div class="col-6 fst-italic label-isalus-small text-isalus-primary"> 
            {{formData.footerDisplay}}
        </div>  
        <div class="col-6 text-end"> 
            <span class="pe-2">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Excel"  
                    :text="'Export'"  
                    :tooltip="'Export Report.'"
                    :size="ButtonDisplaySize.Small" 
                    :disabled="formData.buttons.ExportDisabled"
                    :performAction="exportReport" />
            </span>
            <ActionButtonV2 
                :action="ActionButtonStyle.Close"  
                :text="'Close'"  
                :tooltip="'Close Report.'"
                :size="ButtonDisplaySize.Small" 
                :performAction="cancelProcess" />
        </div>  
    </div>
</template>
